import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);

  const roleLabel = useMemo(() => {
    if (!user?.role) return "";
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  }, [user]);

  const displayName = useMemo(() => {
    if (!user) return "";
    return user.username || user.name || user.email || "User";
  }, [user]);

  const avatarLabel = displayName ? displayName.charAt(0).toUpperCase() : "U";
  const isSpecialUser = user?.email === "ratnanjali@islorg.com";

  const cards = useMemo(
    () => [
      {
        key: "chatbot",
        title: "AI Chatbot",
        description:
          "Interact with our intelligent assistant for queries and support.",
        icon: RobotIcon,
        onClick: () => navigate("/chatbot"),
        visible: true,
      },
      // {
      //   key: "writing",
      //   title: "Writing Assistant",
      //   description:
      //     "Enhance your writing with AI-powered assistance and suggestions.",
      //   icon: WritingIcon,
      //   onClick: () => navigate("/writing-assistant"),
      //   visible: true,
      // },
      {
        key: "users",
        title: "User Management",
        description: "Manage user roles, access, and class data efficiently.",
        icon: UsersIcon,
        onClick: () => navigate("/user-management"),
        visible: user?.role === "admin",
      },
      {
        key: "drive",
        title: "Drive Chatbot",
        description: "Get your answers straight from ISI's G-Drive.",
        icon: CloudIcon,
        onClick: () => navigate("/drive-chatbot"),
        visible: user?.role === "admin" || isSpecialUser,
      },
      {
        key: "train",
        title: "Train Chatbot",
        description: "Access the dedicated training assistant for curated datasets.",
        icon: TrainIcon,
        onClick: () => navigate("/train-chatbot"),
        visible: isSpecialUser,
      },
      {
        key: "document-center",
        title: "Document Center",
        description: "Standardize AI-ready content and format tutor materials.",
        icon: DocumentIcon,
        onClick: () => navigate("/document-center"),
        visible: true,
      },
    ],
    [isSpecialUser, navigate, user]
  );

  if (!user) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading...</div>;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden text-slate-600">
      <div className="flex shrink-0 flex-col gap-2 border-b border-slate-200 bg-white/70 px-6 py-8 backdrop-blur-sm md:px-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Dashboard</p>
            <h1 className="mt-1 text-3xl font-semibold text-slate-800">Welcome back, {displayName}</h1>
            <p className="mt-2 text-sm text-slate-500">Here's what you can do today.</p>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-lg font-semibold text-purple-600">
              {avatarLabel}
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-slate-700">{displayName}</p>
              {roleLabel && (
                <p className="text-xs uppercase tracking-wide text-slate-400">{roleLabel}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-10 md:px-10">
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {cards
            .filter(({ visible }) => visible)
            .map(({ key, title, description, icon: Icon, onClick }) => (
              <button
                key={key}
                type="button"
                onClick={onClick}
                className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-purple-200 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
              >
                <div className="flex items-start justify-between">
                  <div className="rounded-xl bg-purple-50 p-3">
                    <Icon className="h-7 w-7 text-purple-500" />
                  </div>
                  <ArrowIcon className="h-5 w-5 text-slate-300" />
                </div>
                <h2 className="mt-6 text-lg font-semibold text-slate-800">{title}</h2>
                <p className="mt-2 text-sm text-slate-500">{description}</p>
                <span className="mt-6 inline-flex items-center text-sm font-medium text-purple-600">
                  Explore <ArrowIcon className="ml-2 h-4 w-4" />
                </span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

function RobotIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.75v-3m0 3a5.25 5.25 0 015.25 5.25v3.75a2.25 2.25 0 01-2.25 2.25h-6a2.25 2.25 0 01-2.25-2.25V12A5.25 5.25 0 0112 6.75zM9 12h.008v.008H9V12zm6 0h.008v.008H15V12z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18h7.5" />
    </svg>
  );
}

function WritingIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 5.25A2.25 2.25 0 015.25 3h6a2.25 2.25 0 012.25 2.25v13.5A2.25 2.25 0 0111.25 21h-6A2.25 2.25 0 013 18.75V5.25z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.25 7.5l6 6-4.5 4.5H9.75V12l4.5-4.5z"
      />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 17v-.5A3.5 3.5 0 0012.5 13h-7A3.5 3.5 0 002 16.5V17m18 0v-.5a3.5 3.5 0 00-2.87-3.44M16 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function CloudIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 20a4 4 0 01-.88-7.906 5 5 0 019.763-1.707A4.5 4.5 0 0117.5 20H7z"
      />
    </svg>
  );
}

function ArrowIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function TrainIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 19l-1.5 2M16 19l1.5 2M6 7.5h12M6 12h12M9 7.5v-2A2.5 2.5 0 0111.5 3h1A2.5 2.5 0 0115 5.5v2"
      />
      <rect x="4" y="7.5" width="16" height="8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function DocumentIcon({ className }) {
  return (
    <svg 
      className={className} 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      strokeWidth="1.5" 
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}
